import type { UVGenerator } from 'three'
import {
  DoubleSide,
  ExtrudeGeometry,
  Group,
  LinearSRGBColorSpace,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  RepeatWrapping,
  Shape,
  ShapeGeometry,
  TextureLoader,
  Vector2,
} from 'three'

import { getResourceUrl } from '../systems/resources'

const planckLength = 2500
const planckWidth = 500

const stairWoodUVGenerator: UVGenerator = {
  generateTopUV: (_geometry, vertices, indexA, indexB, indexC) => {
    // Top face (end caps) - map texture based on XY coordinates
    const aX = vertices[indexA * 3] ?? 0
    const aY = vertices[indexA * 3 + 1] ?? 0
    const bX = vertices[indexB * 3] ?? 0
    const bY = vertices[indexB * 3 + 1] ?? 0
    const cX = vertices[indexC * 3] ?? 0
    const cY = vertices[indexC * 3 + 1] ?? 0

    return [
      new Vector2(aX / planckLength, aY / planckWidth),
      new Vector2(bX / planckLength, bY / planckWidth),
      new Vector2(cX / planckLength, cY / planckWidth),
    ]
  },

  generateSideWallUV: (_geometry, vertices, indexA, indexB, indexC, indexD) => {
    // Extract the 4 points of the quad
    const aX = vertices[indexA * 3] ?? 0
    const aY = vertices[indexA * 3 + 1] ?? 0
    const aZ = vertices[indexA * 3 + 2] ?? 0

    const bX = vertices[indexB * 3] ?? 0
    const bY = vertices[indexB * 3 + 1] ?? 0
    const bZ = vertices[indexB * 3 + 2] ?? 0

    const cX = vertices[indexC * 3] ?? 0
    const cY = vertices[indexC * 3 + 1] ?? 0
    const cZ = vertices[indexC * 3 + 2] ?? 0

    const dX = vertices[indexD * 3] ?? 0
    const dY = vertices[indexD * 3 + 1] ?? 0
    const dZ = vertices[indexD * 3 + 2] ?? 0

    const isLongEdge = aY === bY && cY === dY

    if (isLongEdge) {
      return [
        new Vector2(aX / planckLength, aZ / planckWidth),
        new Vector2(bX / planckLength, bZ / planckWidth),
        new Vector2(cX / planckLength, cZ / planckWidth),
        new Vector2(dX / planckLength, dZ / planckWidth),
      ]
    }

    return [
      new Vector2(aZ / planckLength, aY / planckWidth),
      new Vector2(bZ / planckLength, bY / planckWidth),
      new Vector2(cZ / planckLength, cY / planckWidth),
      new Vector2(dZ / planckLength, dY / planckWidth),
    ]
  },
}

interface DeckProps {
  length: number
  leftAngle: number
  rightAngle: number
}

const woodTexture = new TextureLoader().load(getResourceUrl('/models/dark_wood.jpg'), texture => {
  texture.colorSpace = LinearSRGBColorSpace

  woodTexture.wrapS = RepeatWrapping
  woodTexture.wrapT = RepeatWrapping

  woodTexture.repeat.set(1, 1)
})

export const createDeckSectionObject = (props: DeckProps) => {
  const { length, leftAngle, rightAngle } = props

  const material = new MeshStandardMaterial({
    roughness: 0.8,
    metalness: 0,
    color: 0x6b4f27,
    name: 'deck',
    map: woodTexture,
    roughnessMap: woodTexture,
  })

  const lineShape = new Shape()

  const width = 500

  const leftRemainderAngle = 2 * Math.PI - leftAngle
  const rightRemainderAngle = 2 * Math.PI - rightAngle

  const leftFactor = Math.tan(leftRemainderAngle / 2)
  const rightFactor = Math.tan(rightRemainderAngle / 2)

  const rightOffset = Math.abs(rightFactor) > 0.001 ? width / rightFactor : 0
  const leftOffset = Math.abs(leftFactor) > 0.001 ? width / leftFactor : 0

  lineShape.moveTo(0, 0)
  lineShape.lineTo(length, 0)
  lineShape.lineTo(length - rightOffset, -width)
  lineShape.lineTo(leftOffset, -width)
  lineShape.lineTo(0, 0)

  const extrudeSettings = {
    steps: 1,
    depth: 250,
    bevelEnabled: false,
    UVGenerator: stairWoodUVGenerator,
  }

  const geometry = new ExtrudeGeometry(lineShape, extrudeSettings)
  const mesh = new Mesh(geometry, material)
  mesh.rotateX(Math.PI / 2)

  const group = new Group()

  group.add(mesh)

  return group
}

interface SketchingDeckProps {
  length: number
  balustrade: boolean
}

export const createDeckSectionSketchingObject = (props: SketchingDeckProps) => {
  const { length, balustrade } = props

  const lineShape = new Shape()

  const width = 100
  const backgroundWidth = 300
  const lineColor = 0x000000
  const fadedLineColor = 0x888888

  lineShape.moveTo(0, 0)
  lineShape.lineTo(length, 0)
  lineShape.lineTo(length, -width)
  lineShape.lineTo(0, -width)
  lineShape.lineTo(0, 0)

  const geometry = new ShapeGeometry(lineShape)
  const material = new MeshBasicMaterial({
    color: balustrade ? lineColor : fadedLineColor,
    side: DoubleSide,
    transparent: !balustrade,
  })

  const mesh = new Mesh(geometry, material)
  mesh.position.y = -20 + Math.random()
  mesh.rotateX(Math.PI / 2)

  const backgroundShape = new Shape()
  backgroundShape.moveTo(0, 0)
  backgroundShape.lineTo(length, 0)
  backgroundShape.lineTo(length, -backgroundWidth)
  backgroundShape.lineTo(0, -backgroundWidth)
  backgroundShape.lineTo(0, 0)

  const backgroundGeometry = new ShapeGeometry(backgroundShape)
  const backgroundMaterial = new MeshBasicMaterial({ color: 0xd3d3d3, side: DoubleSide })
  const backgroundMesh = new Mesh(backgroundGeometry, backgroundMaterial)
  backgroundMesh.position.y = -50 + Math.random()
  backgroundMesh.rotateX(Math.PI / 2)

  const group = new Group()
  group.add(backgroundMesh)
  group.add(mesh)

  return group
}
